import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, HeartPulse, House, Leaf } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { ProductCard } from '../components/ProductCard';
import { CategoryCard } from '../components/CategoryCard';
import { categories } from '../data/products';
import { useProducts } from '../hooks/useProducts';

const consciousChoiceBackground = '/conscious-choice-bg.png';
const heroBackground = '/hero-lumenverde-bg.png';

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.seconds === 'number') {
    const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : 0;
    return value.seconds * 1000 + Math.floor(nanos / 1000000);
  }
  return 0;
};

export default function Home() {
  const { products } = useProducts();
  const newProducts = products
    .filter((product) => product.isActive !== false)
    .sort((a, b) => {
      const createdDiff = toMillis(b?.createdAt) - toMillis(a?.createdAt);
      if (createdDiff !== 0) return createdDiff;

      const updatedDiff = toMillis(b?.updatedAt) - toMillis(a?.updatedAt);
      if (updatedDiff !== 0) return updatedDiff;

      return 0;
    })
    .slice(0, 4);

  return (
    <Layout>
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <div
            className="h-full w-full bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${heroBackground})` }}
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/40 to-background/90" />
        </div>

        <div className="container-zinga relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold leading-tight animate-fade-up">
              Lumen Verde
              <br />
              <span className="text-foreground/70 text-xl sm:text-2xl md:text-4xl">
                Tudatos választás,
                <br className="sm:hidden" />
                <span className="hidden sm:inline"> </span>
                egy kattintással
              </span>
            </h1>
            <div
              className="mt-10 flex flex-col sm:flex-row gap-4 justify-center animate-fade-up stagger-2"
              style={{ opacity: 0 }}
            >
              <Link to="/shop" className="btn-primary inline-flex items-center gap-2">
                Vásárlás
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/shop?category=foods&sub=drink" className="btn-secondary inline-flex items-center gap-2">
                Próbáld ki új termékeinket
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-card">
        <div className="container-zinga">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold">Új termékek</h2>
              <p className="mt-2 text-muted-foreground">Legfrissebb újdonságaink</p>
            </div>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 font-medium text-foreground hover:text-foreground/80 transition-colors"
            >
              Összes termék
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {newProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      <section id="story" className="section-padding relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${consciousChoiceBackground})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-background/90" aria-hidden="true" />
        <div className="container-zinga relative z-10">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold">Kategóriák</h2>
            <p className="mt-4 text-muted-foreground">Fedezd fel kínálatunkat</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category) => (
              <CategoryCard key={category.id} {...category} />
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${consciousChoiceBackground})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-background/82" aria-hidden="true" />
        <div className="container-zinga relative z-10">
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="rounded-3xl border border-border/70 bg-background/84 p-6 md:p-8 shadow-soft backdrop-blur-sm">
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">
                Tudatos választás egy kattintással
              </h2>
              <p className="text-foreground/85 leading-relaxed mb-4">
                A{' '}
                <span className="inline-block rounded-md bg-[#E6BD4E] px-1.5 py-0.5 text-foreground">
                  Lumen Verde
                </span>{' '}
                kínálatának többsége hazai gyártóktól érkezik, mert fontosnak tartjuk, hogy olyan
                termékeket válogassunk össze, amelyek közelebb hozzák egymáshoz a minőséget, a megbízható forrást és a
                tudatos döntést.
              </p>
              <p className="text-foreground/85 leading-relaxed">
                A környezettudatosság és az egészség egyaránt központi szempont nálunk: olyan árukat keresünk, amelyek
                támogatják a fenntarthatóbb hétköznapokat, miközben hozzájárulnak az egészségesebb, kiegyensúlyozottabb
                életmódhoz.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="text-center rounded-2xl border border-border/70 bg-background/78 p-5 shadow-soft backdrop-blur-sm">
                <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center mx-auto mb-3">
                  <House className="w-5 h-5" />
                </div>
                <p className="text-sm font-medium">Hazai gyártók</p>
              </div>
              <div className="text-center rounded-2xl border border-border/70 bg-background/78 p-5 shadow-soft backdrop-blur-sm">
                <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Leaf className="w-5 h-5" />
                </div>
                <p className="text-sm font-medium">Környezettudatos választás</p>
              </div>
              <div className="text-center rounded-2xl border border-border/70 bg-background/78 p-5 shadow-soft backdrop-blur-sm">
                <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center mx-auto mb-3">
                  <HeartPulse className="w-5 h-5" />
                </div>
                <p className="text-sm font-medium">Egészségtudatos életmód</p>
              </div>
            </div>
          </div>
        </div>
      </section>

    </Layout>
  );
}
